import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, lastValueFrom, Subject } from "rxjs";

import { ApiService } from "./api.service";
import { ReportFormatDef, ReportTypeDef } from "../constants";
import { CustomReportingPlan } from "../models/custom-reporting-plan.model";
import { MemberPlan } from "../models/member-plan.model";
import { DirectPaymentPlanCycle } from "../models/direct-payment-plan.model";
import {
    IReportOptions,
    I1099ReportOptions,
    IRebateReportOptions,
    IEpnPaymentDetailsReport,
    IAccountingReportOptions,
    IAccountsReceivableReportOptions,
    IDirectPbmPaymentReport,
    IEpnPaymentReport,
    IExportRBCReportOptions,
} from "../interfaces/report-options.interface";
import { IReportResponse } from "../interfaces/report-response.interface";
import { IReportQueueData, ReportQueueData } from "../models/report-queue-data.model";

import { getErrorMessage } from "src/app/shared/util";
import { environment } from "src/environments/environment";
import { AuthService } from "./auth.service";

export type ReportQueueUIState = {
  closed: boolean;
  minimized: boolean;
};

export interface IReportQueueEvent {
  eventType: 'ReportQueued' | 'ReportReady' | 'ReportFailure' | 'ReportProcessing';
  data: {
    reportId: number,
    errorMessage?: string
  }
};

@Injectable({
    providedIn: 'root'
})
export class ReportService extends ApiService {
    private _authService: AuthService;
    protected _http: HttpClient;

    private readonly _reportReadyBlink$ = new BehaviorSubject<boolean>(false);
    public readonly reportReadyBlink$ = this._reportReadyBlink$.asObservable();

    private _reportQueueUiState$ = new BehaviorSubject<ReportQueueUIState>({ closed: true,  minimized: false });
    public readonly reportQueueUiState$ = this._reportQueueUiState$.asObservable();

    private _reportQueueEvent$ = new Subject<IReportQueueEvent>();
    public reportQueueEvent$ = this._reportQueueEvent$.asObservable();

    private reportHubClient: signalR.HubConnection;

    constructor() {
        const _http = inject(HttpClient);

        super(_http, `${environment.apiBase}Report/`);
        
        this._authService = inject(AuthService);
        this._http = _http;
    }

    //#region SignalR Events
    public async initSignalRClient() : Promise<void> {
        if (!(await this._authService.isAuthenticated())) { return; }

        const reportHubClientUri = new URL(`/hub/report/client`, environment.hubBase);
        this.reportHubClient = new signalR.HubConnectionBuilder()
            .withUrl(reportHubClientUri.toString(), {
                accessTokenFactory: () => this._authService.getAccessToken(),
                withCredentials: true
            })
            .withAutomaticReconnect()
            .build();

        this.reportHubClient.on('ReportReady', (reportId: number) => {
            this._reportQueueEvent$.next({
                eventType: 'ReportReady',
                data: { reportId }
            });
        });

        this.reportHubClient.on('ReportProcessing', (reportId: number) => {
            this._reportQueueEvent$.next({
                eventType: 'ReportProcessing',
                data: { reportId }
            });
        });

        this.reportHubClient.on('ReportQueued', (reportId: number) => {
            this._reportQueueEvent$.next({
                eventType: 'ReportQueued',
                data: { reportId }
            });
        });

        this.reportHubClient.on('ReportFailure', (reportId: number, errorMessage: string) => {
            this._reportQueueEvent$.next({
                eventType: 'ReportFailure',
                data: { reportId, errorMessage }
            });
        });

        this.reportHubClient.start().then(() => {
                console.debug('Report hub connection established successfully.');
            }).catch(err => { 
                console.error('Report Hub Error',err); 
            });
    }
    //#endregion

    //#region Report Generation Methods

    private async _generateReport(route: string, options: IReportOptions): Promise<IReportResponse> {
        try {
            const httpResponse$ = this._download({ route, body: options, httpMethod: 'POST' });
            const httpResponse = await lastValueFrom(httpResponse$);

            let reportResponse: IReportResponse
            if (httpResponse.status === 209) {
                this._reportReadyBlink$.next(false);
                reportResponse = {
                    status: 'SentToQueue',
                    payload: await new Response(httpResponse.body).text() 
                }
            } else {
                const contentDisposition = httpResponse.headers.get('content-disposition');

                let filename: string = '';
                if (contentDisposition !== null) {
                    const contentDispositionFilename = contentDisposition.split(';').find(value => value.includes('filename="')) ?? '';
                    if (contentDispositionFilename !== '') {
                        filename = contentDispositionFilename.substring(11, contentDispositionFilename.length - 1) ?? '';
                    }
                }

                if (filename === '') {
                    filename = this._getReportFileName(options);
                }

                reportResponse = {
                    status: 'ReadyForDownload',
                    payload: httpResponse.body as Blob,
                    filename
                };
            }

            return reportResponse;
        } catch (e) {
            console.error(e);
            const errorMessage = getErrorMessage(e);
            throw new Error(`Failed to generate report, Error: ${errorMessage}.`);
        }
    }

    private _getReportFileName(options: IReportOptions, ...fileNameComponents: string[]): string {
        const fileNameBase = ReportTypeDef.asSelectOption(options.reportType!).label ?? 'Download';
        let ext = '.pdf';
        switch (options.reportFormat) {
            case 'PDF':
            case 'SUMMARY':
                ext = '.pdf';
                break;
            case 'EXCEL':
                ext = '.xls';
                break;
            case '835':
                ext = '.835';
                break;
            case 'TAB':
                ext = '.tab';
                break;
        }
        return `${fileNameBase.replace(' ', '_')}_${fileNameComponents.join('_')}${ext}`;
    }

    public async get1099Report(options: I1099ReportOptions): Promise<IReportResponse> {
        const route = 'Generate1099Report';
        options.reportType = ReportTypeDef.TaxDocument1099;
        options.reportFormat = ReportFormatDef.PDF;
        return await this._generateReport(route, options);
    }

    public async getEpnPaymentDetailsReport(options: IEpnPaymentDetailsReport): Promise<IReportResponse> {
        const route = 'GenerateEpnDetailedPaymentReport';
        options.reportType = ReportTypeDef.EpnPaymentDetails;
        return await this._generateReport(route, options);
    }

    public async getDirectPbmPaymentReport(reportOptions: IDirectPbmPaymentReport): Promise<IReportResponse> {
        return await this._generateReport('GenerateDirectPbmPaymentReport', reportOptions);
    }

    public async getEpnPaymentReport(reportOptions: IEpnPaymentReport): Promise<IReportResponse> {
        return await this._generateReport('GenerateEpnPaymentReport', reportOptions);
    }

    public async getAccountsReceivableReport(reportOptions: IAccountsReceivableReportOptions): Promise<IReportResponse> {
        return await this._generateReport('GenerateAccountsReceivableReport', reportOptions);
    }

    public async getTaxAndAccountingReport(reportOptions: IAccountingReportOptions): Promise<IReportResponse> {
        return await this._generateReport('GenerateTaxAndAccountingReport', reportOptions);
    }

    public async getRebatePaymentReport(options: IRebateReportOptions): Promise<IReportResponse> {
        const route = 'GenerateRebateReport';
        options.reportType = ReportTypeDef.Rebate;
        options.reportFormat = ReportFormatDef.PDF;
        return await this._generateReport(route, options);
    }

    public async getBalanceForwardDetailReport(options: IReportOptions): Promise<IReportResponse> {
        const route = 'GenerateBalanceForwardDetailReport';
        options.reportType = ReportTypeDef.Rebate;
        options.reportFormat = ReportFormatDef.PDF;
        return await this._generateReport(route, options);
    }

    public async getCustomReportingPlans(memberIds: number[]): Promise<CustomReportingPlan[]> {
        try {
            const plans$ = this._get<CustomReportingPlan[]>({ route: `CustomReportingPlanList/Members/${memberIds.join()}` });
            const plans = await lastValueFrom(plans$)
            return plans.map(p => new CustomReportingPlan(p))?.sort(CustomReportingPlan.sort) ?? [];
        } catch (e) {
            throw new Error('Failed to retrieve CustomReportingPlans');
        }
    }

    public async getDirectPaymentPlanCycles(memberIds: number[]): Promise<DirectPaymentPlanCycle[]> {
        try {
            const plans$ = this._get<DirectPaymentPlanCycle[]>({ route: `DirectPaymentPlanCycles/Members/${memberIds.join()}` });
            const plans = await lastValueFrom(plans$)
            return plans.map(p => new DirectPaymentPlanCycle(p))?.sort(DirectPaymentPlanCycle.sort) ?? [];
        } catch (e) {
            throw new Error('Failed to retrieve DirectPaymentPlanCycles');
        }
    }

    public async getMemberPlans(memberIds: number[]): Promise<MemberPlan[]> {
        try {
            const route = `MemberPlan/Members/${memberIds.join(',')}`;
            const memberPlans$ = this._get<MemberPlan[]>({ route });
            const memberPlans = await lastValueFrom(memberPlans$);
            return memberPlans?.map(plan => new MemberPlan(plan))?.sort(MemberPlan.sort) ?? [];
        } catch (e) {
            throw new Error('Failed to retrieve MemberPlans.')
        }
    }

    public async getRbcTicketReport(reportOptions: IExportRBCReportOptions): Promise<IReportResponse> {
      return await this._generateReport('GenerateRbcExportTicketReport', reportOptions);
    }

    public setReportReadyBlink(shouldBlink: boolean): void {
      this._reportReadyBlink$.next(shouldBlink);
    }

    public clearReportReadyBlink(): void {
      this._reportReadyBlink$.next(false);
    }
    //#endregion

    //#region Report Queue Methods

    get uiStateSnapshot(): ReportQueueUIState {
        return this._reportQueueUiState$.value; 
    }

    uiOpenOrMaximize(): void {
        this._reportQueueUiState$.next({ closed: false, minimized: false });
    }

    uiToggleMinimized(): void {
        const { closed, minimized } = this.uiStateSnapshot;
        if (closed) {
            this.uiOpenOrMaximize();
        } else {
            this._reportQueueUiState$.next({ closed, minimized: !minimized });
        }
    }

    uiSetMinimized(): void {
        const snapshot = this.uiStateSnapshot;
        if (!snapshot.closed) {
            this._reportQueueUiState$.next({...snapshot});
        }
    }

    uiClose(): void {
        this._reportQueueUiState$.next({ closed: true, minimized: false });
    }

    public async getAllReportQueueData(): Promise<ReportQueueData[]> {
        const reportQueueData$ = this._get<IReportQueueData[]>({ route: 'GetAllUserReportQueueData' });
        const reportQueueData = await lastValueFrom(reportQueueData$);
        return reportQueueData.map(r => new ReportQueueData(r));
    }

    public async getRecentReportQueueData(): Promise<ReportQueueData[]> {
        const reportQueueData$ = this._get<IReportQueueData[]>({ route: 'GetRecentUserReportQueueData' });
        const reportQueueData = await lastValueFrom(reportQueueData$);
        return reportQueueData.map(r => new ReportQueueData(r));
    }

    public async getReportQueueData(reportQueueDataId: number): Promise<ReportQueueData> {
        const reportQueueData$ = this._get<IReportQueueData>({ route: 'GetReportQueueData', body: { reportQueueDataId }});
        const reportQueueData = await lastValueFrom(reportQueueData$);
        return new ReportQueueData(reportQueueData);
    }

    public async downloadReport(reportQueueDataId: number): Promise<IReportResponse> {
        try {
            const httpResponse$ = this._download({ route: "DownloadReport", body: { reportQueueDataId }, httpMethod: 'GET' });
            const httpResponse = await lastValueFrom(httpResponse$);

            const contentDisposition = httpResponse.headers.get('content-disposition');

            let filename: string = '';
            if (contentDisposition !== null) {
                const contentDispositionFilename = contentDisposition.split(';').find(value => value.includes('filename="')) ?? '';
                if (contentDispositionFilename !== '') {
                    filename = contentDispositionFilename.substring(11, contentDispositionFilename.length - 1) ?? '';
                }
            } else {
                const report = await this.getReportQueueData(reportQueueDataId);
                filename = report.fileName
            }

            return {
                status: 'ReadyForDownload',
                payload: httpResponse.body as Blob,
                filename
            };
        } catch (e) {
            console.error(e);
            const errorMessage = getErrorMessage(e);
            throw new Error(`Failed to download report, Error: ${errorMessage}.`);
        }
    }

    public async retryReport(reportQueueDataId: number): Promise<void> {
      try {
        const retry$ = this._post<void>({
          route: 'RetryReport',
          body: { reportQueueDataId }
        });

        await lastValueFrom(retry$);
      } catch (e) {
        console.error(e);
        const errorMessage = getErrorMessage(e);
        throw new Error(`Failed to retry report, Error: ${errorMessage}.`);
      }
    }
    //#endregion
}
